import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { CalendarView } from '../../calendar/CalendarView';
import { invalidateAllCalendarActionItems, useCalendarActionItems } from '../../calendar/hooks/useCalendarActionItems';
import { useCalendarUndatedActionItems } from '../../calendar/hooks/useCalendarUndatedActionItems';
import { CalendarItem } from '../../calendar/types';
import { useCalendarNavigation } from '../../calendar/useCalendarNavigation';
import { actionItemToCalendarItem } from '../../calendar/utils/normalize';
import { THEME } from '../../../components-v2/Theme/theme';
import { ActionItemDetailsFlyout } from '../ActionItemDetailsFlyout';

const styleSheet = StyleSheet.create({
	container: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		minHeight: 0,
		gap: THEME.spacing.md,
		padding: `${THEME.spacing.md}px 0`,
	},
});

interface IProps {
	/** Assignee scope from the page's shared header (undefined = everyone's action items). */
	assignedToUserId?: string;
	/** Free-text search from the page's shared header. */
	search?: string;
}

/**
 * Calendar view for the Action Items page: the shared {@link CalendarView} surface showing action
 * items by due date (with undated items surfaced on today). Search + scope come from the page's
 * shared header so they drive the list and calendar identically.
 */
export const ActionItemsCalendarView: React.FC<IProps> = ({ assignedToUserId, search }) => {
	const nav = useCalendarNavigation();
	// Roll overdue / surface undated action items onto today only when today is in the visible window.
	const todayInWindow = React.useMemo(() => {
		const t = Date.now();
		return nav.range.start.getTime() <= t && t <= nav.range.end.getTime();
	}, [nav.range]);

	const { actionItems, isLoading } = useCalendarActionItems({ range: nav.range, assignedToUserId, search });
	const { undatedActionItems, hasMore: undatedHasMore } = useCalendarUndatedActionItems({
		assignedToUserId,
		search,
		enabled: todayInWindow,
	});

	const items = React.useMemo<CalendarItem[]>(() => {
		const list: CalendarItem[] = [];
		for (const actionItem of actionItems) {
			const item = actionItemToCalendarItem(actionItem, { rollOverdueToToday: todayInWindow });
			if (item) {
				list.push(item);
			}
		}
		if (todayInWindow) {
			for (const actionItem of undatedActionItems) {
				const item = actionItemToCalendarItem(actionItem, { showUndatedOnToday: true });
				if (item) {
					list.push(item);
				}
			}
		}
		return list;
	}, [actionItems, undatedActionItems, todayInWindow]);

	// Search both lists: undated items come from a separate query, not `actionItems`.
	const [selectedId, setSelectedId] = React.useState<string | null>(null);
	const [isFlyoutOpen, setIsFlyoutOpen] = React.useState(false);
	const selectedActionItem = React.useMemo(
		() => (selectedId ? ([...actionItems, ...undatedActionItems].find(a => a.id === selectedId) ?? null) : null),
		[selectedId, actionItems, undatedActionItems]
	);
	// Reset the open flag if the selected item leaves the live list, so no stale open state lingers.
	React.useEffect(() => {
		if (!selectedActionItem) {
			setIsFlyoutOpen(false);
		}
	}, [selectedActionItem]);
	const onSelectItem = React.useCallback((item: CalendarItem) => {
		if (item.actionItem?.id) {
			setSelectedId(item.actionItem.id);
			setIsFlyoutOpen(true);
		}
	}, []);

	return (
		<div className={css(styleSheet.container)}>
			<CalendarView
				nav={nav}
				items={items}
				isLoading={isLoading}
				onSelectItem={onSelectItem}
				undatedHasMore={undatedHasMore}
			/>

			{selectedActionItem ? (
				<ActionItemDetailsFlyout
					isOpen={isFlyoutOpen}
					setIsOpen={setIsFlyoutOpen}
					actionItem={selectedActionItem}
					onChanged={() => invalidateAllCalendarActionItems()}
				/>
			) : null}
		</div>
	);
};
